import { intercomService } from "@services/intercomService";
import { WSInfoBox, WSList } from "@wingspanhq/fe-component-library";
import { StakeholderSubjectType } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { queriesComplianceEntities, queriesStakeholders } from "@query";
import { useSession } from "@hooks/useSession";
import { useFocusedFeatures } from "../../../../../FocusedFeatures";
import { DataView } from "../../../../../shared/components/DataView";
import { FormPartialDataPerson } from "../../../../Onboarding/components/FormPartialPerson";
import { selectorComplianceEntityTaxVerification } from "../../../../../shared/selectors/selectorComplianceEntityTaxVerification";
import { PATH_ONBOARDING_VERIFICAIONS_TAX } from "../../../../Onboarding";
import { ReviewPerson } from "../../../../Onboarding/components/ReviewPerson";
import { useIsTaxDocumentsExist } from "../../../../TaxDocuments/utils/useIsTaxDocumentsExist";
import { usePayoutMethodMissingData } from "../../../components/AlertPayoutMethodMissingData";
import { DataViewTaxpayerInformation } from "../../../components/DataViewTaxpayerInformation";
import { alertMissingDataProps } from "../../../constants/alerts";
import { useModalEditAddress } from "../components/FormEditAddress";
import { useModalEditDBA } from "../components/FormEditDBA";
import { useModalEditWebsite } from "../components/FormEditWebsite";

type Props = {};

export const RouteBusinessInformation: React.FC<Props> = () => {
  const { accountId } = useSession();
  const history = useHistory();
  const focusedFeatures = useFocusedFeatures();
  const showOldTaxDocuments = useIsTaxDocumentsExist(2023);

  const queryComplianceEntities =
    queriesComplianceEntities.listComplianceEntities.useQuery({});
  const queryStakeholders =
    queriesStakeholders.listAccountStakeholders.useQuery({ accountId });

  const modalEditDBA = useModalEditDBA();
  const modalEditWebsite = useModalEditWebsite();
  const modalEditAddress = useModalEditAddress();

  const payoutMethodMissingData = usePayoutMethodMissingData();

  const entities = queryComplianceEntities.data?.data;
  const businessEntity = entities?.find(entity => !entity.subject);

  const representativeEntity = useMemo(() => {
    const principal = queryStakeholders.data?.data?.find(
      stakeholder =>
        stakeholder.subjectType === StakeholderSubjectType.PERSON &&
        stakeholder.isPrincipal
    );

    if (!principal?.accountSuppliedComplianceEntityId) {
      return undefined;
    }

    return entities?.find(
      entity => entity.id === principal.accountSuppliedComplianceEntityId
    );
  }, [entities, queryStakeholders.data]);

  const country =
    businessEntity?.jurisdictionCountry ||
    businessEntity?.physicalAddress?.country ||
    "US";

  const address = businessEntity?.physicalAddress;

  if (!queryComplianceEntities.data) {
    return null;
  }

  const verificationStatus =
    selectorComplianceEntityTaxVerification(businessEntity);
  const notVerified = verificationStatus === "None";

  const representativeName = representativeEntity?.individualLegalName;
  const representativeAddress = representativeEntity?.physicalAddress;
  const representativeFormData: Partial<FormPartialDataPerson> = {
    firstName: representativeName?.givenName,
    middleName: representativeName?.middleNames,
    lastName: representativeName?.familyName,
    birthday: representativeEntity?.dateOfBirth,
    country:
      representativeEntity?.jurisdictionCountry ||
      representativeAddress?.country,
    address: {
      addressLine1: representativeAddress?.line1 || "",
      addressLine2: representativeAddress?.line2 || "",
      city: representativeAddress?.city || "",
      state: representativeAddress?.state || null,
      postalCode: representativeAddress?.postalCode || ""
    }
  };
  const representativeTaxIdProvided = (
    representativeEntity?.taxIdentifiers ?? []
  ).some(taxId => taxId.hasValue);

  return (
    <WSList gap="2XL">
      <WSInfoBox title="Please ensure your information is up to date and accurate">
        <ol>
          <li>Click edit to edit your information.</li>
          <li>
            Your account type and fields with a green checkmark cannot be edited
            once your account has been verified.
          </li>
          <li>To make changes to verified fields, click “Request update”.</li>
          {showOldTaxDocuments ||
          focusedFeatures.showSideNavTaxDocumentsSettings ? (
            <li>
              To view your tax form delivery preferences, go to{" "}
              <a
                onClick={() => history.push("/member/tax-documents")}
                target="_self"
              >
                Tax Documents
              </a>
            </li>
          ) : null}
        </ol>
      </WSInfoBox>

      <DataViewTaxpayerInformation
        complianceEntity={businessEntity}
        missingFields={payoutMethodMissingData?.requiredFields}
      />

      <DataView
        title="Business information"
        data={[
          {
            label: {
              text: "Doing business as (DBA)",
              actionPosition: "left",
              action: {
                label: "Edit",
                onClick: () => {
                  modalEditDBA.open();
                }
              }
            },
            value: businessEntity?.doingBusinessAs || undefined
          },
          {
            label: {
              text: "Business website",
              actionPosition: "left",
              action: {
                label: "Edit",
                onClick: () => {
                  modalEditWebsite.open();
                }
              }
            },
            value: businessEntity?.websiteUrl || undefined
          },
          {
            label: {
              text: "Business address",
              actionPosition: "left",
              action: {
                label: "Edit",
                onClick: () => {
                  modalEditAddress.open();
                }
              }
            },
            value: address
              ? [
                  address.line1,
                  address.line2,
                  [address.city, address.state, address.postalCode].join(" ")
                ]
                  .filter(Boolean)
                  .join("\n")
              : undefined,
            fsExclude: true,
            alert:
              payoutMethodMissingData?.requiredFields &&
              payoutMethodMissingData?.requiredFields.length > 0
                ? alertMissingDataProps
                : undefined
          },
          country === "US" || country === "CA"
            ? {
                label: "State/Territory of incorporation",
                value: businessEntity?.formationJurisdiction?.subdivisionCode
              }
            : null
        ]}
      />

      <ReviewPerson
        type="Representative"
        formData={representativeFormData}
        taxIdProvided={representativeTaxIdProvided}
        header={{
          label: "Representative information (You)",
          value: {
            action: notVerified
              ? {
                  label: "Request update",
                  onClick: () => {
                    history.push(PATH_ONBOARDING_VERIFICAIONS_TAX);
                  }
                }
              : {
                  label: "Contact support",
                  onClick: intercomService.open
                }
          }
        }}
      />
    </WSList>
  );
};
